import { UserRole } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext, useEffect, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import {
  useResetTrainingMutation,
  useWaiveTrainingMutation,
} from "store/queries/training";
import { ITrainingRecord } from "types/Training";
import { IUserResponse } from "types/User";

import { CertifiedChip } from "./CertifiedChip";

type ITrainingState = Pick<
  ITrainingRecord,
  "clearCertifiedAt" | "trainingProgress"
>;

const fromUser = (user: IUserResponse): ITrainingState => ({
  clearCertifiedAt: user.clearCertifiedAt ?? null,
  trainingProgress: user.trainingProgress ?? null,
});

interface IUserTrainingStatusProps {
  user: IUserResponse;
}

/**
 * CLE-3015 Clear Certified — the training row on the SuperAdmin user detail page.
 *
 * Shows the "Clear Certified" chip when the user is certified, and for a HospitalStaff user:
 * "Mark trained live" (waive) while not certified, "Reset training" once certified or once any
 * progress exists. Each asks for confirmation first.
 *
 * Hidden entirely unless one of the user's accounts has `features.clearCertifiedTraining` on or
 * the user already has training data — so nothing about the training shows before rollout.
 *
 * The state is kept locally from each mutation's response rather than by refetching the user:
 * a refetch would re-seed (and discard edits in) the user form around it.
 */
export function UserTrainingStatus({ user }: IUserTrainingStatusProps) {
  const { current: toast } = useContext(ToastContext) ?? { current: null };
  const { data: accounts } = useGetAllAccountsQuery();
  const [waiveTraining, { isLoading: waiving }] = useWaiveTrainingMutation();
  const [resetTraining, { isLoading: resetting }] = useResetTrainingMutation();
  const [state, setState] = useState<ITrainingState>(() => fromUser(user));

  useEffect(() => {
    setState(fromUser(user));
  }, [user.id, user.clearCertifiedAt, user.trainingProgress]);

  const flagOnForUser = (user.accountIds ?? []).some(
    (accountId) =>
      accounts?.data?.find((account) => account.id === accountId)?.features
        ?.clearCertifiedTraining === true
  );
  const hasTrainingData = Boolean(
    state.clearCertifiedAt || state.trainingProgress
  );

  if (!flagOnForUser && !hasTrainingData) {
    return null;
  }

  const isHospitalStaff = user.scope === UserRole.HOSPITAL_STAFF;
  const certified = Boolean(state.clearCertifiedAt);
  const showWaive = isHospitalStaff && !certified;
  const showReset = isHospitalStaff && hasTrainingData;

  const run = async (
    action: () => Promise<{ data: ITrainingRecord }>,
    success: string
  ) => {
    try {
      const { data } = await action();
      setState({
        clearCertifiedAt: data.clearCertifiedAt,
        trainingProgress: data.trainingProgress,
      });
      toast?.show({ severity: "success", summary: success });
    } catch {
      toast?.show({
        severity: "error",
        summary: "Something went wrong",
        detail: "The training status was not changed.",
      });
    }
  };

  const onWaive = () =>
    confirmDialog({
      header: "Mark trained live?",
      message: `${user.name} will be marked Clear Certified and the training banner will stop showing for them.`,
      acceptLabel: "Mark trained live",
      rejectLabel: "Cancel",
      accept: () =>
        run(() => waiveTraining(user.id).unwrap(), "Marked Clear Certified"),
    });

  const onReset = () =>
    confirmDialog({
      header: "Reset training?",
      message: `This clears ${user.name}'s training progress and certificate. The training banner will show again.`,
      acceptLabel: "Reset training",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () =>
        run(() => resetTraining(user.id).unwrap(), "Training reset"),
    });

  return (
    <div className="field col-12 mt-4" data-testid="user-training-status">
      <h4 className="m-0 mb-2 flex align-items-center gap-2">
        Clear Certified training
        <CertifiedChip certifiedAt={state.clearCertifiedAt} />
      </h4>
      {!certified && (
        <p className="m-0 mb-2 text-sm text-gray-600">
          {state.trainingProgress ? "In progress" : "Not started"}
        </p>
      )}
      <div className="flex gap-2">
        {showWaive && (
          <Button
            type="button"
            className="p-button-sm p-button-outlined"
            label="Mark trained live"
            icon="pi pi-check"
            loading={waiving}
            disabled={waiving || resetting}
            onClick={onWaive}
          />
        )}
        {showReset && (
          <Button
            type="button"
            className="p-button-sm p-button-outlined p-button-danger"
            label="Reset training"
            icon="pi pi-refresh"
            loading={resetting}
            disabled={waiving || resetting}
            onClick={onReset}
          />
        )}
      </div>
    </div>
  );
}
